import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import dayjs from "dayjs";
import { toast } from "sonner";

import { bangTinApi } from "src/1/apis/bang_tin.api";

import CreatePostModal from "./CreatePostModal";

import "../editor.css";

export default function PostList() {
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState(null);
  const [postToDelete, setPostToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Gọi API lấy bài viết: mỗi trang hiển thị đúng 1 bài (size: 1), BE tự ưu tiên bài ghim trước
  const { data: postsData, isLoading } = useQuery(
    ["news-feed-posts", activePage, searchKeyword],
    () =>
      bangTinApi.getPosts({
        page: activePage,
        size: 1,
        keyword: searchKeyword || undefined,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );

  // Trích xuất danh sách bài viết và tổng số bài viết linh hoạt theo mọi cấu trúc BE trả về
  const { posts, total } = useMemo(() => {
    if (!postsData) return { posts: [], total: 0 };

    // Trường hợp 1: postsData là mảng trực tiếp: [ post1, post2 ]
    if (Array.isArray(postsData)) {
      return { posts: postsData, total: postsData.length };
    }

    // Trường hợp 2: postsData có mảng nằm trong data, items, rows, list
    let list = [];
    if (Array.isArray(postsData.data)) {
      list = postsData.data;
    } else if (Array.isArray(postsData?.data?.data)) {
      list = postsData.data.data;
    } else if (Array.isArray(postsData.items)) {
      list = postsData.items;
    } else if (Array.isArray(postsData.rows)) {
      list = postsData.rows;
    } else if (Array.isArray(postsData.list)) {
      list = postsData.list;
    } else if (Array.isArray(postsData.records)) {
      list = postsData.records;
    }

    // Nếu list vẫn rỗng nhưng postsData có id (BE trả về 1 object bài viết duy nhất)
    if (list.length === 0 && postsData.id) {
      list = [postsData];
    }

    const totalCount =
      postsData.total ??
      postsData?.data?.total ??
      postsData.count ??
      postsData.total_items ??
      postsData.total_records ??
      list.length;

    return { posts: list, total: Number(totalCount) || list.length };
  }, [postsData]);

  const totalPages = total > 0 ? total : 1;
  const currentPost = posts[0] || null;

  // Xử lý tìm kiếm
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setActivePage(1);
    setSearchKeyword(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchKeyword("");
    setActivePage(1);
  };

  // Mở modal tạo bài viết mới
  const handleOpenCreate = () => {
    setPostToEdit(null);
    setIsModalOpen(true);
  };

  // Mở modal sửa bài viết
  const handleOpenEdit = (post) => {
    setPostToEdit(post);
    setIsModalOpen(true);
  };

  // Submit tạo mới hoặc sửa bài viết
  const handleSubmitPost = async (formData) => {
    try {
      if (postToEdit) {
        await bangTinApi.updatePost(postToEdit.id, formData);
        toast.success("Cập nhật bài viết thành công!");
      } else {
        await bangTinApi.createPost(formData);
        toast.success("Đăng bài viết mới thành công!");
        setActivePage(1);
        setSearchInput("");
        setSearchKeyword("");
      }
      setIsModalOpen(false);
      setPostToEdit(null);
      await queryClient.invalidateQueries(["news-feed-posts"]);
      await queryClient.refetchQueries(["news-feed-posts"]);
      queryClient.invalidateQueries(["news-feed-sidebar-pinned"]);
    } catch (err) {
      toast.error(err?.message || "Có lỗi xảy ra khi lưu bài viết");
      throw err;
    }
  };

  // Đổi trạng thái ghim bài viết
  const handleTogglePin = async (post) => {
    try {
      const nextPin = !post.is_pin;
      await bangTinApi.updatePost(post.id, { is_pin: nextPin });
      toast.success(
        nextPin ? "Đã ghim bài viết lên đầu!" : "Đã bỏ ghim bài viết!",
      );
      await queryClient.invalidateQueries(["news-feed-posts"]);
      await queryClient.refetchQueries(["news-feed-posts"]);
      queryClient.invalidateQueries(["news-feed-sidebar-pinned"]);
    } catch (err) {
      toast.error("Không thể thay đổi trạng thái ghim bài viết");
    }
  };

  // Xác nhận xóa bài viết
  const handleConfirmDelete = async () => {
    if (!postToDelete) return;
    try {
      setIsDeleting(true);
      await bangTinApi.deletePost(postToDelete.id);
      toast.success("Đã xóa bài viết thành công!");
      setPostToDelete(null);
      if (activePage > 1 && activePage === total) {
        setActivePage((p) => p - 1);
      }
      await queryClient.invalidateQueries(["news-feed-posts"]);
      await queryClient.refetchQueries(["news-feed-posts"]);
      queryClient.invalidateQueries(["news-feed-sidebar-pinned"]);
    } catch (err) {
      toast.error("Không thể xóa bài viết");
    } finally {
      setIsDeleting(false);
    }
  };

  const authorName = currentPost?.created_by || "Quản trị kho";
  const authorInitials = authorName.substring(0, 2).toUpperCase();
  const createdAtFormatted = currentPost?.created_at
    ? dayjs(currentPost.created_at).format("DD/MM/YYYY HH:mm")
    : "";

  return (
    <>
      <div className="!space-y-4">
        {/* Thanh tiêu đề & Tìm kiếm bài viết */}
        <div className="!bg-white !rounded-xl !p-4 !shadow-xs !border !border-slate-200/90 !space-y-3">
          <div className="!flex !flex-col sm:!flex-row sm:!items-center !justify-between !gap-3">
            <div className="!flex !items-center !gap-2.5">
              <div className="!w-9 !h-9 !rounded-lg !bg-blue-50 !border !border-blue-200 !flex !items-center !justify-center !text-[#053c7f] !shrink-0">
                <svg
                  className="!w-5 !h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="!text-base !font-extrabold !text-slate-900 !leading-tight">
                  Bảng tin & Thông báo kho
                </h2>
                <p className="!text-xs sm:!text-sm !text-slate-500">
                  {total > 0
                    ? `Hiển thị bài viết ${activePage} trên tổng số ${total} bài`
                    : "Chưa có bài viết nào"}
                </p>
              </div>
            </div>

            {/* Nút "+ Đăng bài viết" */}
            <button
              onClick={handleOpenCreate}
              className="!inline-flex !items-center !justify-center !gap-2 !bg-[#053c7f] hover:!bg-[#074b9e] !text-white !text-sm !font-bold !px-4 !py-2 !rounded-xl !shadow-xs !transition-colors !self-start sm:!self-auto !cursor-pointer"
            >
              <span>+&nbsp;&nbsp;Đăng bài viết</span>
            </button>
          </div>

          {/* Ô tìm kiếm theo từ khóa */}
          <form
            onSubmit={handleSearchSubmit}
            className="!flex !items-center !gap-2 !pt-1"
          >
            <div className="!relative !flex-1">
              <svg
                className="!w-4 !h-4 !text-slate-400 !absolute !left-3 !top-1/2 !-translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Tìm theo tiêu đề bài viết..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="!w-full !pl-9 !pr-8 !py-2 !text-sm !bg-slate-50 !border !border-slate-200 !rounded-lg focus:!outline-none focus:!bg-white focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-100 !transition-colors"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="!absolute !right-2.5 !top-1/2 !-translate-y-1/2 !text-slate-400 hover:!text-slate-600 !text-sm !cursor-pointer !px-1"
                >
                  &times;
                </button>
              )}
            </div>
            <button
              type="submit"
              className="!px-4 !py-2 !bg-slate-100 hover:!bg-slate-200 !text-slate-700 !font-bold !text-sm !rounded-lg !border !border-slate-200 !transition-colors !cursor-pointer !shrink-0"
            >
              Tìm kiếm
            </button>
          </form>
        </div>

        {/* Vùng hiển thị 1 bài viết của trang hiện tại */}
        <div>
          {isLoading ? (
            <div className="!bg-white !rounded-xl !p-6 !border !border-slate-200/90 !space-y-4 !animate-pulse">
              <div className="!flex !items-center !gap-3">
                <div className="!w-10 !h-10 !rounded-xl !bg-slate-200" />
                <div className="!space-y-1.5 !flex-1">
                  <div className="!h-4 !bg-slate-200 !rounded !w-1/4" />
                  <div className="!h-3 !bg-slate-200 !rounded !w-1/6" />
                </div>
              </div>
              <div className="!h-6 !bg-slate-200 !rounded !w-3/4" />
              <div className="!space-y-2">
                <div className="!h-4 !bg-slate-200 !rounded !w-full" />
                <div className="!h-4 !bg-slate-200 !rounded !w-5/6" />
                <div className="!h-4 !bg-slate-200 !rounded !w-4/6" />
              </div>
            </div>
          ) : !currentPost ? (
            <div className="!bg-white !rounded-xl !p-12 !text-center !border !border-slate-200/90 !shadow-xs">
              <div className="!w-14 !h-14 !rounded-2xl !bg-blue-50 !border !border-blue-100 !flex !items-center !justify-center !mx-auto !text-blue-600 !text-2xl !mb-3">
                📝
              </div>
              <h3 className="!text-base !font-bold !text-slate-800">
                Không tìm thấy bài viết nào
              </h3>
              <p className="!text-sm !text-slate-500 !mt-1 !max-w-sm !mx-auto">
                {searchKeyword
                  ? `Không có bài viết nào phù hợp với từ khóa "${searchKeyword}".`
                  : "Bảng tin kho hiện tại chưa có bài viết. Hãy đăng bài viết thông báo mới."}
              </p>
              <button
                onClick={handleOpenCreate}
                className="!mt-4 !inline-flex !items-center !gap-1.5 !bg-[#053c7f] hover:!bg-[#074b9e] !text-white !text-sm !font-bold !px-4 !py-2 !rounded-lg !transition-colors !cursor-pointer"
              >
                <span>+&nbsp;&nbsp;Đăng bài viết mới</span>
              </button>
            </div>
          ) : (
            <article className="!bg-white !rounded-xl !shadow-xs !border !border-slate-200/90 !overflow-hidden hover:!shadow-md !transition-shadow !duration-200">
              {/* Header bài viết: Người tạo, Ngày tạo, Tag trạng thái ghim, Hành động */}
              <div className="!p-4 md:!p-5 !flex !items-start !justify-between !border-b !border-slate-100 !gap-3">
                <div className="!flex !items-center !gap-3 !min-w-0">
                  <div className="!space-y-1 !min-w-0">
                    <div className="!flex !flex-wrap !items-center !gap-2">
                      <span className="!font-bold !text-slate-900 !text-sm">
                        {authorName}
                      </span>

                      {/* Tag / Icon hiển thị trạng thái ghim bài viết */}
                      {currentPost.is_pin ? (
                        <span className="!inline-flex !items-center !gap-1.5 !bg-amber-50 !text-amber-800 !text-xs !font-bold !px-2.5 !py-0.5 !rounded-full !border !border-amber-200 !shadow-2xs">
                          <span>📌</span>
                          <span>Đã ghim</span>
                        </span>
                      ) : (
                        <span className="!inline-flex !items-center !gap-1 !bg-slate-50 !text-slate-500 !text-xs !font-medium !px-2.5 !py-0.5 !rounded-full !border !border-slate-200">
                          <span>📄</span>
                          <span>Bài viết</span>
                        </span>
                      )}
                    </div>

                    {/* Ngày tạo */}
                    <div className="!text-slate-500 font-medium !flex !items-center !gap-1.5">
                      <svg
                        className="!w-3.5 !h-3.5 !text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>{createdAtFormatted}</span>
                    </div>
                  </div>
                </div>

                {/* Nút hành động: Ghim, Sửa, Xóa */}
                <div className="!flex !items-center !gap-1.5 !shrink-0">
                  <button
                    onClick={() => handleTogglePin(currentPost)}
                    title={
                      currentPost.is_pin
                        ? "Bỏ ghim bài viết"
                        : "Ghim bài viết lên đầu"
                    }
                    className={`!p-1.5 !rounded-lg !border !transition-colors !cursor-pointer ${
                      currentPost.is_pin
                        ? "!bg-amber-100 !text-amber-800 !border-amber-300 hover:!bg-amber-200"
                        : "!bg-slate-50 hover:!bg-slate-100 !text-slate-500 !border-slate-200"
                    }`}
                  >
                    <span className="!text-sm">📌</span>
                  </button>

                  <button
                    onClick={() => handleOpenEdit(currentPost)}
                    title="Chỉnh sửa bài viết"
                    className="!p-1.5 !bg-slate-50 hover:!bg-blue-50 !text-slate-600 hover:!text-blue-700 !border !border-slate-200 hover:!border-blue-300 !rounded-lg !transition-colors !cursor-pointer"
                  >
                    <svg
                      className="!w-4 !h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={() => setPostToDelete(currentPost)}
                    title="Xóa bài viết"
                    className="!p-1.5 !bg-slate-50 hover:!bg-red-50 !text-slate-600 hover:!text-red-700 !border !border-slate-200 hover:!border-red-300 !rounded-lg !transition-colors !cursor-pointer"
                  >
                    <svg
                      className="!w-4 !h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Thân bài viết: Tiêu đề & Nội dung định dạng HTML */}
              <div className="!p-4 md:!p-5 !space-y-4">
                <h3 className="!text-lg md:!text-xl !font-extrabold !text-slate-900 !leading-snug">
                  {currentPost.title}
                </h3>

                {/* Nội dung dạng HTML content (render an toàn qua dangerouslySetInnerHTML) */}
                <div
                  className="news-feed-html-content !text-sm !text-slate-700 !leading-relaxed !break-words !space-y-2 [&_p]:!mb-2 [&_ul]:!list-disc [&_ul]:!pl-8 [&_ul]:!my-2 [&_ol]:!list-decimal [&_ol]:!pl-8 [&_ol]:!my-2 [&_li]:!list-item [&_li]:!my-1 [&_a]:!text-blue-600 [&_a]:!underline [&_img]:!max-w-full [&_img]:!rounded-lg [&_h1]:!text-xl [&_h1]:!font-bold [&_h2]:!text-lg [&_h2]:!font-bold [&_h3]:!text-base [&_h3]:!font-bold [&_table]:!w-full [&_table]:!border-collapse [&_th]:!border [&_th]:!p-2 [&_td]:!border [&_td]:!p-2"
                  dangerouslySetInnerHTML={{
                    __html: currentPost.content || "",
                  }}
                />

                {/* Danh sách file đính kèm của bài viết */}
                {currentPost.files && currentPost.files.length > 0 && (
                  <div className="!pt-4 !border-t !border-slate-100 !space-y-2.5">
                    <div className="!flex !items-center !gap-2 !text-xs !font-bold !text-slate-700 !uppercase !tracking-wider">
                      <svg
                        className="!w-4 !h-4 !text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                        />
                      </svg>
                      <span>File đính kèm ({currentPost.files.length})</span>
                    </div>

                    <div className="!grid !grid-cols-1 sm:!grid-cols-2 !gap-2.5">
                      {currentPost.files.map((file, fIdx) => {
                        const fileName =
                          file.original_name ||
                          file.name ||
                          file.file_name ||
                          `File đính kèm ${fIdx + 1}`;
                        const fileUrl = file.url || file.path || "#";
                        const isImage =
                          file.type?.startsWith?.("image/") ||
                          /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                        const isPdf = /\.pdf$/i.test(fileName);
                        const isExcel = /\.(xlsx|xls|csv)$/i.test(fileName);

                        return (
                          <a
                            key={file.id || fIdx}
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="!flex !items-center !justify-between !gap-2.5 !p-2.5 !bg-slate-50 hover:!bg-blue-50/60 !border !border-slate-200 hover:!border-blue-300 !rounded-xl !transition-all !group"
                          >
                            <div className="!flex !items-center !gap-2.5 !min-w-0">
                              {isImage && fileUrl !== "#" ? (
                                <img
                                  src={fileUrl}
                                  alt=""
                                  className="!w-8 !h-8 !rounded-lg !object-cover !shrink-0 !border !border-slate-200"
                                />
                              ) : (
                                <div
                                  className={`!w-8 !h-8 !rounded-lg !flex !items-center !justify-center !shrink-0 !font-bold !text-[10px] ${
                                    isPdf
                                      ? "!bg-red-100 !text-red-700"
                                      : isExcel
                                        ? "!bg-emerald-100 !text-emerald-700"
                                        : "!bg-blue-100 !text-blue-700"
                                  }`}
                                >
                                  {isPdf ? "PDF" : isExcel ? "XLS" : "FILE"}
                                </div>
                              )}
                              <div className="!min-w-0">
                                <span
                                  className="!block !font-semibold !text-xs !text-slate-800 group-hover:!text-blue-700 !truncate"
                                  title={fileName}
                                >
                                  {fileName}
                                </span>
                              </div>
                            </div>

                            <div className="!p-1 !text-slate-400 group-hover:!text-blue-600 !shrink-0">
                              <svg
                                className="!w-4 !h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </article>
          )}
        </div>

        {/* Điều hướng chuyển bài (Pagination: mỗi trang 1 bài) */}
        {totalPages > 1 && (
          <div className="!flex !items-center !justify-between !gap-3 !pt-1">
            <button
              onClick={() => setActivePage((p) => Math.max(1, p - 1))}
              disabled={activePage === 1}
              className="!inline-flex !items-center !gap-1.5 !px-3.5 !py-2 !rounded-xl !border !border-slate-200 !bg-white hover:!bg-slate-50 !text-slate-700 !text-xs sm:!text-sm !font-bold disabled:!opacity-40 disabled:!cursor-not-allowed !cursor-pointer !transition-colors !shadow-2xs"
            >
              <svg
                className="!w-4 !h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>Bài trước</span>
            </button>

            <div className="!flex !items-center !gap-2 !text-xs sm:!text-sm !text-slate-600 !font-semibold">
              <span>Bài</span>
              <span className="!px-2.5 !py-1 !rounded-lg !bg-[#053c7f] !text-white !font-bold">
                {activePage}
              </span>
              <span>/ {total}</span>
            </div>

            <button
              onClick={() => setActivePage((p) => Math.min(totalPages, p + 1))}
              disabled={activePage === totalPages}
              className="!inline-flex !items-center !gap-1.5 !px-3.5 !py-2 !rounded-xl !border !border-slate-200 !bg-white hover:!bg-slate-50 !text-slate-700 !text-xs sm:!text-sm !font-bold disabled:!opacity-40 disabled:!cursor-not-allowed !cursor-pointer !transition-colors !shadow-2xs"
            >
              <span>Bài tiếp</span>
              <svg
                className="!w-4 !h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Modal xác nhận xóa bài viết */}
        {postToDelete && (
          <div className="!fixed !inset-0 !z-50 !flex !items-center !justify-center !p-4 !bg-slate-900/60 !backdrop-blur-xs">
            <div className="!bg-white !rounded-2xl !p-6 !shadow-2xl !border !border-slate-200 !w-full !max-w-md !space-y-4">
              <div className="!flex !items-center !gap-3 !text-red-600">
                <div className="!w-10 !h-10 !rounded-full !bg-red-100 !flex !items-center !justify-center !shrink-0">
                  <svg
                    className="!w-5 !h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="!text-base !font-bold !text-slate-900">
                    Xác nhận xóa bài viết
                  </h3>
                  <p className="!text-sm !text-slate-500">
                    Hành động này sẽ xóa bài viết khỏi bảng tin kho
                  </p>
                </div>
              </div>

              <p className="!text-sm !text-slate-600 !bg-slate-50 !p-3 !rounded-lg !border !border-slate-200">
                Bạn có chắc chắn muốn xóa bài viết: <br />
                <strong className="!text-slate-800 !font-semibold">
                  {postToDelete.title}
                </strong>
                ?
              </p>

              <div className="!flex !items-center !justify-end !gap-2.5 !pt-2">
                <button
                  type="button"
                  onClick={() => setPostToDelete(null)}
                  className="!px-4 !py-2 !text-sm !font-semibold !text-slate-600 hover:!bg-slate-100 !rounded-lg !transition-colors !cursor-pointer"
                >
                  Hủy bỏ
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="!px-4 !py-2 !text-sm !font-bold !text-white !bg-red-600 hover:!bg-red-700 !rounded-lg !shadow-sm !transition-colors !cursor-pointer disabled:!opacity-50"
                >
                  {isDeleting ? "Đang xóa..." : "Xác nhận xóa"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Đăng / Sửa bài viết */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPostToEdit(null);
        }}
        onSubmit={handleSubmitPost}
        postToEdit={postToEdit}
      />
    </>
  );
}
